import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Add,
  ArticleOutlined,
  Delete,
  Edit,
  GroupOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import API from "api/axiosInstance";
import Header from "components/Header";
import FloatingAlert from "components/FloatingAlert";
import KonfirmasiModal from "components/Modals/KonfirmasiModal";
import { setMessage, setStatus } from "state";

const PERMISSION_GROUPS = [
  {
    label: "Utama",
    description: "Halaman ringkasan utama aplikasi",
    slugs: ["dashboard"],
  },
  {
    label: "Karyawan & Organisasi",
    description: "Data karyawan dan struktur organisasi",
    slugs: ["karyawan", "karyawanbaru", "departemen", "divisi", "jabatan", "jenjang"],
  },
  {
    label: "Absensi",
    description: "Kehadiran dan rekap absensi",
    slugs: ["absensihariini", "rekapabsensiharian", "rekapabsensibulanan"],
  },
  {
    label: "Keuangan",
    description: "Setoran, tabungan, dan pencapaian",
    slugs: ["setoran", "pencapaian", "penggajian", "tabungan", "rekaptabungan", "setortabungan", "tariktabungan"],
  },
  {
    label: "Operasional",
    description: "Stock opname, alokasi, dan dokumen transaksi",
    slugs: ["stockopname", "pesertaso", "alokasidc", "bukuwarkat"],
  },
  {
    label: "Komunikasi",
    description: "Informasi dan masukan karyawan",
    slugs: ["peringatan", "notifikasi", "panduan", "aspirasi"],
  },
  {
    label: "Administrasi Sistem",
    description: "Pengaturan hak akses aplikasi",
    slugs: ["role_management"],
  },
];

const EMPTY_ROLE = {
  name: "",
  slug: "",
  description: "",
  permissionIds: [],
};

const EMPTY_USER = {
  name: "",
  email: "",
  password: "",
  role_id: "",
  active: true,
};

const EMPTY_PAGE = {
  name: "",
  slug: "",
  description: "",
};

const groupLabelFor = (slug) =>
  PERMISSION_GROUPS.find((group) => group.slugs.includes(slug))?.label || "Lainnya";

const RoleManagement = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { status, message } = useSelector((state) => state.global);

  const [tab, setTab] = useState(0);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [openPageModal, setOpenPageModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  const [roleForm, setRoleForm] = useState(EMPTY_ROLE);
  const [editRoleId, setEditRoleId] = useState(null);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [editUserId, setEditUserId] = useState(null);
  const [pageForm, setPageForm] = useState(EMPTY_PAGE);
  const [editPageId, setEditPageId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permissionsRes, usersRes] = await Promise.all([
        API.get("/api/roles/roles"),
        API.get("/api/roles/permissions"),
        API.get("/api/roles/users"),
      ]);
      setRoles(rolesRes.data.data || []);
      setPermissions(permissionsRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } catch (error) {
      dispatch(setMessage(error.response?.data?.error || "Gagal mengambil data role management."));
      dispatch(setStatus("failed"));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const groupedPermissions = useMemo(() => {
    const grouped = PERMISSION_GROUPS.map((group) => ({ ...group, items: [] }));
    const knownSlugs = new Set(PERMISSION_GROUPS.flatMap((group) => group.slugs));
    const otherGroup = { label: "Lainnya", description: "Permission tambahan", items: [] };

    permissions.forEach((permission) => {
      const group = grouped.find((item) => item.slugs.includes(permission.slug));
      if (group) group.items.push(permission);
      else if (!knownSlugs.has(permission.slug)) otherGroup.items.push(permission);
    });

    return [...grouped, otherGroup].filter((group) => group.items.length > 0);
  }, [permissions]);

  const activeUsers = users.filter((user) => user.active).length;

  const handleOpenRoleModal = (role = null) => {
    if (role) {
      setEditRoleId(role.id);
      setRoleForm({
        name: role.name || "",
        slug: role.slug || "",
        description: role.description || "",
        permissionIds: role.permissions?.map((permission) => permission.id) || [],
      });
    } else {
      setEditRoleId(null);
      setRoleForm(EMPTY_ROLE);
    }
    setOpenRoleModal(true);
  };

  const handlePermissionToggle = (permissionId) => {
    setRoleForm((previous) => ({
      ...previous,
      permissionIds: previous.permissionIds.includes(permissionId)
        ? previous.permissionIds.filter((id) => id !== permissionId)
        : [...previous.permissionIds, permissionId],
    }));
  };

  const handleSelectAllPermissions = () => {
    setRoleForm((previous) => ({
      ...previous,
      permissionIds: permissions.map((permission) => permission.id),
    }));
  };

  const handleClearPermissions = () => {
    setRoleForm((previous) => ({ ...previous, permissionIds: [] }));
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim() || (!editRoleId && !roleForm.slug.trim())) {
      dispatch(setMessage("Nama role dan slug wajib diisi."));
      dispatch(setStatus("failed"));
      return;
    }

    try {
      if (editRoleId) {
        await API.put(`/api/roles/roles/${editRoleId}`, {
          name: roleForm.name.trim(),
          description: roleForm.description,
          permissionIds: roleForm.permissionIds,
        });
      } else {
        await API.post("/api/roles/roles", {
          ...roleForm,
          name: roleForm.name.trim(),
          slug: roleForm.slug.trim().toLowerCase(),
        });
      }
      dispatch(setMessage(editRoleId ? "Role berhasil diupdate." : "Role berhasil dibuat."));
      dispatch(setStatus("success"));
      setOpenRoleModal(false);
      fetchAll();
    } catch (error) {
      dispatch(setMessage(error.response?.data?.error || "Gagal menyimpan role."));
      dispatch(setStatus("failed"));
    }
  };

  const handleOpenUserModal = (user = null) => {
    if (user) {
      setEditUserId(user.id);
      setUserForm({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role_id: user.role_id || "",
        active: Boolean(user.active),
      });
    } else {
      setEditUserId(null);
      setUserForm(EMPTY_USER);
    }
    setOpenUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.role_id || (!editUserId && !userForm.password)) {
      dispatch(setMessage("Nama, email, role, dan password wajib diisi."));
      dispatch(setStatus("failed"));
      return;
    }

    try {
      if (editUserId) {
        await API.put(`/api/roles/users/${editUserId}`, userForm);
      } else {
        await API.post("/api/roles/users", userForm);
      }
      dispatch(setMessage(editUserId ? "User berhasil diupdate." : "User berhasil dibuat."));
      dispatch(setStatus("success"));
      setOpenUserModal(false);
      fetchAll();
    } catch (error) {
      dispatch(setMessage(error.response?.data?.error || "Gagal menyimpan user."));
      dispatch(setStatus("failed"));
    }
  };

  // ============ PAGE (PERMISSION) HANDLERS ============
  const handleOpenPageModal = (page = null) => {
    if (page) {
      setEditPageId(page.id);
      setPageForm({
        name: page.name || "",
        slug: page.slug || "",
        description: page.description || "",
      });
    } else {
      setEditPageId(null);
      setPageForm(EMPTY_PAGE);
    }
    setOpenPageModal(true);
  };

  const handleSavePage = async () => {
    if (!pageForm.name.trim() || (!editPageId && !pageForm.slug.trim())) {
      dispatch(setMessage("Nama dan kode halaman wajib diisi."));
      dispatch(setStatus("failed"));
      return;
    }

    try {
      if (editPageId) {
        await API.put(`/api/roles/permissions/${editPageId}`, {
          name: pageForm.name.trim(),
          description: pageForm.description,
        });
      } else {
        await API.post("/api/roles/permissions", {
          name: pageForm.name.trim(),
          slug: pageForm.slug.trim(),
          description: pageForm.description,
        });
      }
      dispatch(setMessage(editPageId ? "Halaman berhasil diupdate." : "Halaman berhasil ditambahkan."));
      dispatch(setStatus("success"));
      setOpenPageModal(false);
      fetchAll();
    } catch (error) {
      dispatch(setMessage(error.response?.data?.error || "Gagal menyimpan halaman."));
      dispatch(setStatus("failed"));
    }
  };

  const handleDelete = async () => {
    try {
      const endpoints = {
        role: `/api/roles/roles/${deleteTarget.id}`,
        user: `/api/roles/users/${deleteTarget.id}`,
        permission: `/api/roles/permissions/${deleteTarget.id}`,
      };
      const labels = { role: "Role", user: "User", permission: "Halaman" };
      await API.delete(endpoints[deleteType]);
      dispatch(setMessage(`${labels[deleteType]} berhasil dihapus.`));
      dispatch(setStatus("success"));
      setOpenDeleteModal(false);
      fetchAll();
    } catch (error) {
      dispatch(setMessage(error.response?.data?.error || "Gagal menghapus data."));
      dispatch(setStatus("failed"));
    }
  };

  const openDeleteConfirmation = (target, type) => {
    setDeleteTarget(target);
    setDeleteType(type);
    setOpenDeleteModal(true);
  };

  const closeAlert = () => {
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 70px)",
        backgroundColor: theme.palette.background.default,
        p: { xs: 2, sm: 3, md: "1.5rem 2.5rem" },
      }}
    >
      <Header
        title="Role Management"
        subtitle="Atur siapa yang dapat menggunakan setiap halaman website"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mt: 3,
        }}
      >
        <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[100] }}>Total Role</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: theme.palette.secondary[200] }}>{roles.length}</Typography>
          <Typography variant="caption" color="text.secondary">Kelompok hak akses</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[100] }}>User Aktif</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: theme.palette.secondary[200] }}>{activeUsers}</Typography>
          <Typography variant="caption" color="text.secondary">Dari {users.length} user website</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.secondary[100] }}>Halaman Tersedia</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: theme.palette.secondary[200] }}>{permissions.length}</Typography>
          <Typography variant="caption" color="text.secondary">Permission yang bisa dipilih</Typography>
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ mt: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="fullWidth"
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Tab icon={<ShieldOutlined />} iconPosition="start" label="Daftar Role" />
          <Tab icon={<GroupOutlined />} iconPosition="start" label="Pengguna Website" />
          <Tab icon={<ArticleOutlined />} iconPosition="start" label="Daftar Halaman" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.secondary[100] }}>Role dan hak akses</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Pilih role untuk melihat atau mengatur halaman yang dapat digunakan.
                </Typography>
              </Box>
              <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => handleOpenRoleModal()}>
                Tambah Role
              </Button>
            </Stack>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                    <TableCell><strong>Nama Role</strong></TableCell>
                    <TableCell><strong>Kode</strong></TableCell>
                    <TableCell><strong>Hak Akses</strong></TableCell>
                    <TableCell><strong>Jumlah User</strong></TableCell>
                    <TableCell align="right"><strong>Aksi</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id} hover>
                      <TableCell>
                        <Typography fontWeight={700}>{role.name}</Typography>
                        {role.description && <Typography variant="caption" color="text.secondary">{role.description}</Typography>}
                      </TableCell>
                      <TableCell><Chip label={role.slug} size="small" color={role.slug === "admin" ? "error" : "default"} /></TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {role.permissions?.slice(0, 4).map((permission) => <Chip key={permission.id} label={permission.name} size="small" variant="outlined" />)}
                          {(role.permissions?.length || 0) > 4 && <Chip label={`+${role.permissions.length - 4} halaman`} size="small" />}
                          {!role.permissions?.length && <Typography variant="body2" color="text.secondary">Belum ada akses</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell>{role.userCount || 0} user</TableCell>
                      <TableCell align="right">
                        <IconButton color="info" size="small" title="Edit role" onClick={() => handleOpenRoleModal(role)}><Edit fontSize="small" /></IconButton>
                        <IconButton color="error" size="small" title="Hapus role" disabled={role.slug === "admin"} onClick={() => openDeleteConfirmation(role, "role")}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && roles.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 4 }}>Belum ada role.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.secondary[100] }}>Pengguna website</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Tambahkan user dan tentukan role yang dimilikinya.</Typography>
              </Box>
              <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => handleOpenUserModal()}>Tambah User</Button>
            </Stack>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                    <TableCell><strong>Nama</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Aksi</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell><Typography fontWeight={700}>{user.name}</Typography></TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell><Chip label={user.role?.name || "-"} size="small" color={user.role?.slug === "admin" ? "error" : "primary"} /></TableCell>
                      <TableCell><Chip label={user.active ? "Aktif" : "Nonaktif"} size="small" color={user.active ? "success" : "default"} /></TableCell>
                      <TableCell align="right">
                        <IconButton color="info" size="small" title="Edit user" onClick={() => handleOpenUserModal(user)}><Edit fontSize="small" /></IconButton>
                        <IconButton color="error" size="small" title="Hapus user" onClick={() => openDeleteConfirmation(user, "user")}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && users.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 4 }}>Belum ada user website.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.secondary[100] }}>Daftar halaman</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Halaman adalah menu yang bisa dibagikan ke role. Kolom "Akses Role" menunjukkan role yang punya akses.
                </Typography>
              </Box>
              <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => handleOpenPageModal()}>Tambah Halaman</Button>
            </Stack>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                    <TableCell><strong>Nama Halaman</strong></TableCell>
                    <TableCell><strong>Kode</strong></TableCell>
                    <TableCell><strong>Kelompok</strong></TableCell>
                    <TableCell><strong>Akses Role</strong></TableCell>
                    <TableCell align="right"><strong>Aksi</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.map((page) => {
                    const pageRoles = roles.filter((role) =>
                      role.permissions?.some((p) => p.id === page.id),
                    );
                    return (
                      <TableRow key={page.id} hover>
                        <TableCell>
                          <Typography fontWeight={700}>{page.name}</Typography>
                          {page.description && <Typography variant="caption" color="text.secondary">{page.description}</Typography>}
                        </TableCell>
                        <TableCell><Chip label={page.slug} size="small" variant="outlined" /></TableCell>
                        <TableCell><Typography variant="body2">{groupLabelFor(page.slug)}</Typography></TableCell>
                        <TableCell>
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {pageRoles.length ? pageRoles.map((role) => (
                              <Chip key={role.id} label={role.name} size="small" color={role.slug === "admin" ? "error" : "default"} />
                            )) : <Chip label="Belum ada role" size="small" color="warning" variant="outlined" />}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton color="info" size="small" title="Edit halaman" onClick={() => handleOpenPageModal(page)}><Edit fontSize="small" /></IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            title="Hapus halaman"
                            disabled={page.slug === "role_management"}
                            onClick={() => openDeleteConfirmation(page, "permission")}
                          ><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && permissions.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 4 }}>Belum ada halaman terdaftar.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Dialog open={openRoleModal} onClose={() => setOpenRoleModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editRoleId ? "Edit Role" : "Tambah Role"}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2.5} sx={{ mt: 0.5 }}>
            <Alert severity="info">Role menentukan halaman yang bisa dibuka oleh user. Centang halaman yang diperlukan, lalu klik Simpan.</Alert>
            <TextField label="Nama Role" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} fullWidth size="small" required />
            {!editRoleId && <TextField label="Kode Role" helperText="Gunakan huruf kecil tanpa spasi, contoh: supervisor" value={roleForm.slug} onChange={(e) => setRoleForm({ ...roleForm, slug: e.target.value.toLowerCase().replace(/\s/g, "_") })} fullWidth size="small" required />}
            <TextField label="Deskripsi (opsional)" value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} fullWidth size="small" multiline rows={2} />
            <Divider />
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>Hak Akses Halaman</Typography>
                <Typography variant="body2" color="text.secondary">{roleForm.permissionIds.length} dari {permissions.length} halaman dipilih</Typography>
              </Box>
              <Stack direction="row" gap={1}>
                <Button size="small" onClick={handleSelectAllPermissions}>Pilih Semua</Button>
                <Button size="small" color="inherit" onClick={handleClearPermissions}>Hapus Semua</Button>
              </Stack>
            </Stack>
            <Stack gap={1.5}>
              {groupedPermissions.map((group) => (
                <Paper key={group.label} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography fontWeight={700}>{group.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{group.description}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, mt: 1 }}>
                    {group.items.map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        control={<Checkbox checked={roleForm.permissionIds.includes(permission.id)} onChange={() => handlePermissionToggle(permission.id)} />}
                        label={<Typography variant="body2">{permission.name}</Typography>}
                      />
                    ))}
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenRoleModal(false)} color="inherit">Batal</Button>
          <Button onClick={handleSaveRole} variant="contained" color="secondary">{editRoleId ? "Simpan Perubahan" : "Buat Role"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openUserModal} onClose={() => setOpenUserModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUserId ? "Edit Pengguna Website" : "Tambah Pengguna Website"}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 0.5 }}>
            <Alert severity="info">User akan dapat membuka halaman sesuai role yang dipilih.</Alert>
            <TextField label="Nama Lengkap" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} fullWidth size="small" required />
            <TextField label="Email Login" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} fullWidth size="small" required />
            <TextField label={editUserId ? "Password baru (opsional)" : "Password"} type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} fullWidth size="small" required={!editUserId} helperText={editUserId ? "Kosongkan jika password tidak ingin diubah." : "Password digunakan untuk login website."} />
            <FormControl fullWidth size="small" required>
              <InputLabel>Role Pengguna</InputLabel>
              <Select value={userForm.role_id} label="Role Pengguna" onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })}>
                {roles.map((role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
              </Select>
              <FormHelperText>Pilih role untuk menentukan hak akses halaman.</FormHelperText>
            </FormControl>
            {editUserId && <FormControlLabel control={<Switch checked={userForm.active} onChange={(e) => setUserForm({ ...userForm, active: e.target.checked })} color="secondary" />} label={userForm.active ? "Akun aktif" : "Akun nonaktif"} />}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenUserModal(false)} color="inherit">Batal</Button>
          <Button onClick={handleSaveUser} variant="contained" color="secondary">{editUserId ? "Simpan Perubahan" : "Buat User"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPageModal} onClose={() => setOpenPageModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editPageId ? "Edit Halaman" : "Tambah Halaman"}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 0.5 }}>
            <Alert severity="info">Halaman harus sesuai dengan menu yang ada di aplikasi. Kode halaman digunakan untuk mencocokkan menu sidebar.</Alert>
            <TextField label="Nama Halaman" value={pageForm.name} onChange={(e) => setPageForm({ ...pageForm, name: e.target.value })} fullWidth size="small" required placeholder="contoh: Data Supplier" />
            {!editPageId && <TextField label="Kode Halaman" helperText="Gunakan huruf kecil tanpa spasi, harus sama dengan path menu, contoh: supplier" value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })} fullWidth size="small" required />}
            <TextField label="Deskripsi (opsional)" value={pageForm.description} onChange={(e) => setPageForm({ ...pageForm, description: e.target.value })} fullWidth size="small" multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPageModal(false)} color="inherit">Batal</Button>
          <Button onClick={handleSavePage} variant="contained" color="secondary">{editPageId ? "Simpan Perubahan" : "Tambah"}</Button>
        </DialogActions>
      </Dialog>

      <KonfirmasiModal
        titleType="Hapus"
        subtitleType={`Menghapus ${deleteType === "role" ? "role" : deleteType === "user" ? "user" : "halaman"}`}
        dataWillProses={deleteTarget?.name || deleteTarget?.email || ""}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onSubmit={handleDelete}
      />

      <FloatingAlert open={message.length > 0} onClose={closeAlert} message={message} severity={status === "failed" ? "error" : status} />
    </Box>
  );
};

export default RoleManagement;
